import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import type { PublicOrganizationProfile } from '@/services/publicOrg';
import { BrowserImage } from '@/components/ui/BrowserImage';
import { PublicChrome } from '@/components/public/PublicChrome';
import { PublicOrgConsumerMenu } from '@/components/public/PublicOrgConsumerMenu';
import { PublicOrgConsumerNavDropdowns } from '@/components/public/PublicOrgConsumerNavDropdowns';
import { OrgConsumerContextSync } from '@/components/public/OrgConsumerContextSync';
import { OrgLoginDialogProvider } from '@/contexts/OrgLoginDialogContext';
import { OrgRegisterDialogProvider } from '@/contexts/OrgRegisterDialogContext';
import { OrgLoginNavigationOpener } from '@/components/public/org/OrgLoginNavigationOpener';
import { OrgJoinClubLink } from '@/components/public/org/OrgJoinClubLink';
import { TenantBrandingProvider } from '@/contexts/AcademyBrandingContext';
import { hasAnyRole } from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { isOrgPublicSignInAvailable } from '@/utils/publicBrowseMode';
import { useEmbedHost } from '@/contexts/EmbedHostContext';

interface PublicOrgLayoutProps {
  profile: PublicOrganizationProfile;
  children: React.ReactNode;
}

type NavItem = {
  to: string;
  label: string;
  icon?: typeof UserPlus;
  show: boolean;
};

function isOrgNavItemActive(
  item: Pick<NavItem, 'to' | 'label'>,
  pathname: string
): boolean {
  if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
    return true;
  }
  if (item.label === 'Lessons' && pathname.includes('/location/')) {
    return true;
  }
  return false;
}

export function PublicOrgLayout({ profile, children }: PublicOrgLayoutProps) {
  const { user } = useAuthStore();
  const { p, fields } = useOrgPaths(profile.organization);
  const location = useLocation();
  const { isEmbedMode } = useEmbedHost();
  const { organization, modules } = profile;

  if (!p || !fields) return <>{children}</>;

  const membershipReturn = `${location.pathname}${location.search}`;

  return (
    <TenantBrandingProvider
      primaryColor={organization.primaryColor ?? undefined}
      secondaryColor={organization.secondaryColor ?? undefined}
    >
      <PublicChrome>
        <OrgRegisterDialogProvider profile={profile} defaultReturnUrl={membershipReturn}>
          <OrgLoginDialogProvider profile={profile} defaultReturnUrl={membershipReturn}>
            <OrgLoginNavigationOpener />
            {isEmbedMode ? (
              <>
                <OrgConsumerContextSync profile={profile} />
                {children}
              </>
            ) : (
              <PublicOrgLayoutBody
                profile={profile}
                organization={organization}
                modules={modules}
                p={p}
                user={user}
              >
                {children}
              </PublicOrgLayoutBody>
            )}
          </OrgLoginDialogProvider>
        </OrgRegisterDialogProvider>
      </PublicChrome>
    </TenantBrandingProvider>
  );
}

function PublicOrgGuestActions({
  membershipReturn,
  joinPath,
  showJoin,
  showSignIn,
  isAuthenticated,
}: {
  membershipReturn: string;
  joinPath: string | null;
  showJoin: boolean;
  showSignIn: boolean;
  isAuthenticated: boolean;
}) {
  const { login } = useOrgPaths();
  const loginPath = showSignIn ? login?.(membershipReturn) : null;

  if (!showJoin && !loginPath) return null;

  return (
    <div className="flex shrink-0 items-center gap-5">
      {loginPath ? (
        <Link
          to={loginPath}
          className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          Sign in
        </Link>
      ) : null}
      {showJoin && joinPath ? (
        isAuthenticated ? (
          <Button size="sm" asChild>
            <Link to={joinPath}>
              <UserPlus className="h-4 w-4" aria-hidden />
              Join the club
            </Link>
          </Button>
        ) : (
          <Button size="sm" asChild>
            <OrgJoinClubLink
              joinPath={joinPath}
              className="inline-flex items-center justify-center gap-2"
            >
              <UserPlus className="h-4 w-4" aria-hidden />
              Join the club
            </OrgJoinClubLink>
          </Button>
        )
      ) : null}
    </div>
  );
}

function PublicOrgLayoutBody({
  profile,
  organization,
  modules,
  p,
  user,
  children,
}: {
  profile: PublicOrganizationProfile;
  organization: PublicOrganizationProfile['organization'];
  modules: PublicOrganizationProfile['modules'];
  p: (subpath: string) => string;
  user: ReturnType<typeof useAuthStore.getState>['user'];
  children: React.ReactNode;
}) {
  const location = useLocation();
  const isConsumer = Boolean(user && hasAnyRole(user, ['guardian', 'student', 'member']));

  const navItems: NavItem[] = [
    { to: p('/lessons'), label: 'Lessons', show: modules.hasAcademy },
    { to: p('/court-hire'), label: 'Court hire', show: modules.courtHireEntitled },
    { to: p('/play'), label: 'Events', show: modules.playEventsEntitled },
  ].filter((item) => item.show);

  const membershipReturn = `${location.pathname}${location.search}`;
  const showJoinClub = modules.membershipOnlineJoinEnabled && !user;
  const showSignIn = !user && isOrgPublicSignInAvailable(modules, profile.academy);

  const navLinkClass = (active: boolean) =>
    cn(
      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
      active
        ? 'text-primary-700 dark:text-primary-300'
        : 'text-muted-foreground hover:text-foreground'
    );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <OrgConsumerContextSync profile={profile} />
      <nav className="border-b border-border bg-card shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <Link to={p('')} className="flex min-w-0 items-center gap-3">
              {organization.logoUrl ? (
                <BrowserImage
                  src={organization.logoUrl}
                  alt={organization.name}
                  className="h-10 w-auto max-w-[140px] object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="truncate text-lg font-bold gradient-text">{organization.name}</span>
              )}
            </Link>

            <div className="flex items-center gap-1">
              <div className="hidden items-center gap-1 md:flex">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isOrgNavItemActive(item, location.pathname);
                  return (
                    <Link key={item.to} to={item.to} className={navLinkClass(active)}>
                      {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
                      {item.label}
                    </Link>
                  );
                })}
                {isConsumer ? <PublicOrgConsumerNavDropdowns navLinkClass={navLinkClass} /> : null}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {isConsumer ? (
                  <PublicOrgConsumerMenu />
                ) : (
                  <PublicOrgGuestActions
                    membershipReturn={membershipReturn}
                    joinPath={p('/join')}
                    showJoin={showJoinClub}
                    showSignIn={showSignIn}
                    isAuthenticated={Boolean(user)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-1 overflow-x-auto border-t border-border py-2 md:hidden">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isOrgNavItemActive(item, location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
                    active
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-muted-foreground'
                  )}
                >
                  {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
                  {item.label}
                </Link>
              );
            })}
            {isConsumer ? (
              <PublicOrgConsumerNavDropdowns
                navLinkClass={(active) =>
                  cn(
                    'flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
                    active
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-muted-foreground'
                  )
                }
                variant="mobile"
              />
            ) : null}
          </div>
        </div>
      </nav>

      <main className="flex-1">{children}</main>
    </div>
  );
}
